import { secureFetch } from '../utils/secureFetch';

const API_BASE_URL = 'https://api.minymol.com';

/**
 * Servicio para gestionar catálogos privados
 */
class PrivateCatalogService {

    /**
     * Obtiene el usuario actual desde localStorage (mismo patrón que MyProducts)
     * @returns {Object|null} Usuario o null
     */
    static getUserFromStorage() {
        try {
            return JSON.parse(localStorage.getItem('usuario'));
        } catch (error) {
            console.error('Error al obtener usuario de localStorage:', error);
            return null;
        }
    }

    /**
     * Obtiene el perfil del proveedor desde localStorage (mismo patrón que MyProducts)
     * @returns {Object|null} Datos del proveedor o null
     */
    static getProviderFromStorage() {
        const user = this.getUserFromStorage();
        return user?.proveedorInfo || null;
    }

    /**
     * Obtiene todos los catálogos del proveedor autenticado
     * @returns {Promise<Array>} Lista de catálogos
     */
    static async getMyCatalogs() {
        try {
            const res = await secureFetch(`${API_BASE_URL}/private-catalogs/my/all`);

            if (res.ok) {
                // Verificar si la respuesta tiene contenido antes de parsear JSON
                const contentLength = res.headers.get('content-length');
                const contentType = res.headers.get('content-type');
                
                // Si no hay contenido o el content-length es 0, retornar array vacío
                if (contentLength === '0' || !contentType?.includes('application/json')) {
                    return [];
                }

                const responseText = await res.text();
                
                // Si el texto está vacío, retornar array vacío
                if (!responseText || responseText.trim() === '') {
                    return [];
                }

                try {
                    const catalogs = JSON.parse(responseText);
                    return Array.isArray(catalogs) ? catalogs : [];
                } catch (parseError) {
                    console.warn('Error al parsear respuesta JSON, retornando array vacío:', parseError);
                    return [];
                }
            } else if (res.status === 404) {
                return []; // No tiene catálogos aún
            } else {
                throw new Error(`Error al obtener catálogos: ${res.status}`);
            }
        } catch (error) {
            console.error('Error en getMyCatalogs:', error);
            // Si es un error de JSON, retornar array vacío en lugar de lanzar error
            if (error.message && error.message.includes('JSON')) {
                return [];
            }
            throw error;
        }
    }

    /**
     * Obtiene el catálogo general (tipo ALL) del proveedor
     * @returns {Promise<Object|null>} Catálogo general o null
     */
    static async getMyCatalog() {
        try {
            const catalogs = await this.getMyCatalogs();
            return catalogs.find(catalog => catalog.type === 'ALL') || null;
        } catch (error) {
            console.error('Error en getMyCatalog:', error);
            throw error;
        }
    }

    /**
     * Crea un nuevo catálogo
     * @param {Object} catalogData - Datos del catálogo
     * @param {string} catalogData.internalName - Nombre interno del catálogo (único)
     * @param {string} catalogData.publicName - Nombre público del catálogo (visible)
     * @param {string} [catalogData.description] - Descripción del catálogo
     * @param {string} [catalogData.enlace] - Enlace del catálogo
     * @param {string} catalogData.type - Tipo de catálogo (ALL, CATEGORY, SUBCATEGORY, CUSTOM)
     * @param {number} [catalogData.categoryId] - ID de categoría (requerido si type es CATEGORY o SUBCATEGORY)
     * @param {number} [catalogData.subcategoryId] - ID de subcategoría (requerido si type es SUBCATEGORY)
     * @param {Array} [catalogData.productIds] - IDs de productos (requerido si type es CUSTOM)
     * @param {string} [catalogData.priceField] - Campo de precio a mostrar (price1, price2, price3)
     * @param {string} [catalogData.telefono] - Teléfono específico del catálogo
     * @returns {Promise<Object>} Catálogo creado
     */
    static async createCatalog({ internalName, publicName, description, enlace, type, categoryId, subcategoryId, subcategoryIds, productIds, priceField, telefono }) {
        try {
            const createData = {
                internalName: internalName.trim(),
                publicName: publicName.trim()
            };
            
            if (description !== undefined) {
                createData.description = description.trim();
            }
            if (enlace !== undefined) {
                createData.enlace = enlace.trim();
            }
            if (priceField !== undefined && priceField !== null && priceField !== '') {
                createData.priceField = priceField;
            }
            if (telefono !== undefined && telefono !== null && telefono.trim() !== '') {
                createData.telefono = telefono.trim();
            }

            let endpoint = '';
            let method = '';

            // Determinar endpoint según el tipo
            if (type === 'ALL') {
                endpoint = `${API_BASE_URL}/private-catalogs`;
                method = 'PUT';
            } else if (type === 'CATEGORY') {
                endpoint = `${API_BASE_URL}/private-catalogs/by-category`;
                method = 'POST';
                createData.categoryId = categoryId;
            } else if (type === 'SUBCATEGORY') {
                endpoint = `${API_BASE_URL}/private-catalogs/by-subcategory`;
                method = 'POST';
                // subcategoryIds (multi) tiene prioridad; si no, usar subcategoryId como array
                if (subcategoryIds && subcategoryIds.length > 0) {
                    createData.subcategoryIds = subcategoryIds;
                } else {
                    createData.subcategoryIds = [subcategoryId];
                }
            } else if (type === 'CUSTOM') {
                endpoint = `${API_BASE_URL}/private-catalogs/custom`;
                method = 'POST';
                createData.productIds = productIds;
            } else {
                throw new Error('Tipo de catálogo no válido');
            }

            const res = await secureFetch(endpoint, {
                method,
                body: JSON.stringify(createData),
            });

            if (res.ok) {
                return await res.json();
            } else {
                const error = await res.json();
                throw new Error(error.message || 'Error al crear catálogo');
            }
        } catch (error) {
            console.error('Error en createCatalog:', error);
            throw error;
        }
    }

    /**
     * Actualiza un catálogo existente
     * @param {number} catalogId - ID del catálogo
     * @param {Object} catalogData - Datos a actualizar
     * @param {string} [catalogData.internalName] - Nombre interno del catálogo
     * @param {string} [catalogData.publicName] - Nombre público del catálogo
     * @param {string} [catalogData.description] - Descripción del catálogo
     * @param {string} [catalogData.enlace] - Enlace del catálogo
     * @param {string} [catalogData.priceField] - Campo de precio a mostrar
     * @param {string} [catalogData.telefono] - Teléfono específico del catálogo
     * @returns {Promise<Object>} Catálogo actualizado
     */
    static async updateCatalog(catalogId, { internalName, publicName, description, enlace, priceField, telefono }) {
        try {
            const updateData = {};
            
            if (internalName !== undefined) {
                updateData.internalName = internalName.trim();
            }
            if (publicName !== undefined) {
                updateData.publicName = publicName.trim();
            }
            if (description !== undefined) {
                updateData.description = description.trim();
            }
            if (enlace !== undefined) {
                updateData.enlace = enlace;
            }
            if (priceField !== undefined) {
                updateData.priceField = priceField;
            }
            if (telefono !== undefined) {
                updateData.telefono = telefono ? telefono.trim() : null;
            }

            const res = await secureFetch(`${API_BASE_URL}/private-catalogs/${catalogId}`, {
                method: 'PUT',
                body: JSON.stringify(updateData),
            });

            if (res.ok) {
                return await res.json();
            } else {
                const error = await res.json();
                throw new Error(error.message || 'Error al actualizar catálogo');
            }
        } catch (error) {
            console.error('Error en updateCatalog:', error);
            throw error;
        }
    }

    /**
     * Crea o actualiza el catálogo general (tipo ALL) - mantiene compatibilidad
     * @param {Object} catalogData - Datos del catálogo
     * @param {string} [catalogData.internalName] - Nombre interno del catálogo
     * @param {string} [catalogData.publicName] - Nombre público del catálogo
     * @param {string} [catalogData.description] - Descripción del catálogo
     * @param {string} [catalogData.enlace] - Enlace del catálogo
     * @returns {Promise<Object>} Catálogo actualizado
     */
    static async updateGeneralCatalog({ internalName, publicName, description, enlace }) {
        try {
            const generalCatalog = await this.getMyCatalog();
            
            if (generalCatalog) {
                // Actualizar catálogo existente
                return await this.updateCatalog(generalCatalog.id, { internalName, publicName, description, enlace });
            } else {
                // Crear nuevo catálogo general
                const newCatalog = await this.createCatalog({ 
                    internalName: internalName || 'Mi Catálogo General',
                    publicName: publicName || 'Mi Catálogo General',
                    description, 
                    type: 'ALL' 
                });
                
                // Si hay enlace, actualizarlo
                if (enlace) {
                    return await this.updateCatalog(newCatalog.id, { enlace });
                }
                
                return newCatalog;
            }
        } catch (error) {
            console.error('Error en updateGeneralCatalog:', error);
            throw error;
        }
    }

    /**
     * Genera una signed URL para subir un banner
     * @param {File} file - Archivo de imagen
     * @returns {Promise<Object>} Objeto con signedUrl y finalUrl
     */
    static async generateBannerSignedUrl(file) {
        try {
            const mimeType = file.type;
            const filename = file.name;

            const res = await secureFetch(`${API_BASE_URL}/private-catalogs/banner/signed-url`, {
                method: 'POST',
                body: JSON.stringify({ filename, mimeType }),
            });

            if (res.ok) {
                return await res.json();
            } else {
                const error = await res.json();
                throw new Error(error.message || 'Error al generar signed URL');
            }
        } catch (error) {
            console.error('Error en generateBannerSignedUrl:', error);
            throw error;
        }
    }

    /**
     * Actualiza la URL del banner del proveedor
     * @param {string} bannerUrl - URL final del banner
     * @returns {Promise<Object>} Respuesta del servidor
     */
    static async updateBannerUrl(bannerUrl) {
        try {
            const res = await secureFetch(`${API_BASE_URL}/private-catalogs/banner/update-url`, {
                method: 'POST',
                body: JSON.stringify({ bannerUrl }),
            });

            if (res.ok) {
                return await res.json();
            } else {
                const error = await res.json();
                throw new Error(error.message || 'Error al actualizar URL del banner');
            }
        } catch (error) {
            console.error('Error en updateBannerUrl:', error);
            throw error;
        }
    }

    /**
     * Elimina un catálogo
     * @param {number} catalogId - ID del catálogo
     * @returns {Promise<void>}
     */
    static async deleteCatalog(catalogId) {
        try {
            const res = await secureFetch(`${API_BASE_URL}/private-catalogs/${catalogId}`, {
                method: 'DELETE',
            });

            if (!res.ok) {
                const error = await res.json();
                throw new Error(error.message || 'Error al eliminar catálogo');
            }
        } catch (error) {
            console.error('Error en deleteCatalog:', error);
            throw error;
        }
    }

    /**
     * Obtiene productos de un catálogo público (para comerciantes)
     * @param {number} providerId - ID del proveedor
     * @returns {Promise<Object>} Datos del catálogo con productos
     */
    static async getCatalogProducts(providerId) {
        try {
            const res = await fetch(`${API_BASE_URL}/catalog/${providerId}/products`);

            if (res.ok) {
                return await res.json();
            } else {
                const error = await res.json();
                throw new Error(error.message || 'Error al obtener productos del catálogo');
            }
        } catch (error) {
            console.error('Error en getCatalogProducts:', error);
            throw error;
        }
    }

    /**
     * Obtiene solo los IDs de productos del catálogo con información del proveedor
     * Soporta búsqueda por nombre o descripción
     * @param {string} catalogId - ID del catálogo
     * @param {string} [searchTerm] - Término de búsqueda opcional
     * @returns {Promise<Object>} Objeto con info del proveedor, catálogo y array de IDs
     */
    static async getCatalogProductIds(catalogId, searchTerm = '') {
        try {
            const url = searchTerm 
                ? `${API_BASE_URL}/catalog/by-catalog/${catalogId}/product-ids?search=${encodeURIComponent(searchTerm)}`
                : `${API_BASE_URL}/catalog/by-catalog/${catalogId}/product-ids`;
            
            const res = await fetch(url);

            if (res.ok) {
                return await res.json();
            } else {
                const error = await res.json();
                throw new Error(error.message || 'Error al obtener IDs de productos');
            }
        } catch (error) {
            console.error('Error en getCatalogProductIds:', error);
            throw error;
        }
    }

    /**
     * Obtiene productos completos por sus IDs (para infinite scroll)
     * @param {Array<string>} ids - Array de IDs de productos
     * @param {string} catalogId - ID del catálogo (opcional, para filtrar precios)
     * @returns {Promise<Array>} Array de productos completos
     */
    static async getProductsByIds(ids, catalogId = null) {
        try {
            if (!ids || ids.length === 0) {
                return [];
            }

            const body = { ids };
            if (catalogId) {
                body.catalogId = catalogId;
            }

            const res = await fetch(`${API_BASE_URL}/catalog/products/previews`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(body)
            });

            if (res.ok) {
                return await res.json();
            } else {
                const error = await res.json();
                throw new Error(error.message || 'Error al obtener productos por IDs');
            }
        } catch (error) {
            console.error('Error en getProductsByIds:', error);
            throw error;
        }
    }

    /**
     * Obtiene productos de un catálogo específico por su ID
     * @param {string} catalogId - ID del catálogo específico
     * @returns {Promise<Object>} Datos del catálogo con productos filtrados
     */
    static async getCatalogProductsByCatalogId(catalogId) {
        try {
            const res = await fetch(`${API_BASE_URL}/catalog/by-catalog/${catalogId}/products`);

            if (res.ok) {
                return await res.json();
            } else {
                const error = await res.json();
                throw new Error(error.message || 'Error al obtener productos del catálogo específico');
            }
        } catch (error) {
            console.error('Error en getCatalogProductsByCatalogId:', error);
            throw error;
        }
    }

    /**
     * Obtiene información del catálogo público (para comerciantes)
     * @param {number} providerId - ID del proveedor
     * @returns {Promise<Object>} Información del catálogo
     */
    static async getCatalogInfo(providerId) {
        try {
            const res = await fetch(`${API_BASE_URL}/catalog/${providerId}`);

            if (res.ok) {
                return await res.json();
            } else {
                const error = await res.json();
                throw new Error(error.message || 'Error al obtener información del catálogo');
            }
        } catch (error) {
            console.error('Error en getCatalogInfo:', error);
            throw error;
        }
    }

    /**
     * Obtiene categorías disponibles para crear catálogos
     * @returns {Promise<Array>} Lista de categorías
     */
    static async getCategories() {
        try {
            const res = await secureFetch(`${API_BASE_URL}/categories`);

            if (res.ok) {
                return await res.json();
            } else {
                throw new Error('Error al obtener categorías');
            }
        } catch (error) {
            console.error('Error en getCategories:', error);
            throw error;
        }
    }

    /**
     * Obtiene categorías disponibles para el proveedor
     * @returns {Promise<Object>} Categorías y opciones disponibles
     */
    static async getAvailableCategories() {
        try {
            const res = await secureFetch(`${API_BASE_URL}/private-catalogs/available-categories`);

            if (res.ok) {
                return await res.json();
            } else {
                const error = await res.json();
                throw new Error(error.message || 'Error al obtener categorías disponibles');
            }
        } catch (error) {
            console.error('Error en getAvailableCategories:', error);
            throw error;
        }
    }

    /**
     * Obtiene categorías con productos del proveedor
     * @returns {Promise<Object>} Estructura de categorías con productos
     */
    static async getMyCategories() {
        try {
            const res = await secureFetch(`${API_BASE_URL}/private-catalogs/my-categories`);

            if (res.ok) {
                return await res.json();
            } else {
                const error = await res.json();
                throw new Error(error.message || 'Error al obtener mis categorías');
            }
        } catch (error) {
            console.error('Error en getMyCategories:', error);
            throw error;
        }
    }

    /**
     * Obtiene productos del proveedor para el selector de catálogos personalizados
     * Solo devuelve: id, name, imageUrl
     * @returns {Promise<Array>} Lista de productos disponibles
     */
    static async getProductsForSelection() {
        try {
            const res = await secureFetch(`${API_BASE_URL}/private-catalogs/products-for-selection`);

            if (res.ok) {
                return await res.json();
            } else if (res.status === 404) {
                return [];
            } else {
                throw new Error(`Error al obtener productos: ${res.status}`);
            }
        } catch (error) {
            console.error('Error en getProductsForSelection:', error);
            throw error;
        }
    }

    /**
     * Obtiene subcategorías de una categoría específica
     * @param {number} categoryId - ID de la categoría
     * @returns {Promise<Array>} Lista de subcategorías
     */
    static async getSubcategories(categoryId) {
        try {
            const res = await secureFetch(`${API_BASE_URL}/categories/${categoryId}/subcategories`);

            if (res.ok) {
                return await res.json();
            } else {
                throw new Error('Error al obtener subcategorías');
            }
        } catch (error) {
            console.error('Error en getSubcategories:', error);
            throw error;
        }
    }

    /**
     * Obtiene las opciones disponibles para crear catálogos según el proveedor
     * @returns {Promise<Object>} Objeto con categorías y shouldShowCategoryCatalogs
     */
    static async getAvailableOptions() {
        try {
            const res = await secureFetch(`${API_BASE_URL}/private-catalogs/available-categories`);

            if (res.ok) {
                return await res.json();
            } else {
                throw new Error('Error al obtener opciones disponibles');
            }
        } catch (error) {
            console.error('Error en getAvailableOptions:', error);
            throw error;
        }
    }

    /**
     * Sube un banner usando el flujo de signed URLs
     * @param {File} file - Archivo de imagen
     * @returns {Promise<Object>} Objeto con la URL final del banner
     */
    static async uploadBanner(file) {
        try {
            // 1. Generar signed URL
            const { signedUrl, finalUrl } = await this.generateBannerSignedUrl(file);

            // 2. Subir archivo directamente a S3
            const uploadRes = await fetch(signedUrl, {
                method: 'PUT',
                body: file,
                headers: {
                    'Content-Type': file.type,
                },
            });

            if (!uploadRes.ok) {
                throw new Error('Error al subir archivo a S3');
            }

            // 3. Actualizar URL en la base de datos
            await this.updateBannerUrl(finalUrl);

            return { bannerUrl: finalUrl };
        } catch (error) {
            console.error('Error en uploadBanner:', error);
            throw error;
        }
    }

    /**
     * Sube un banner para el catálogo general (mantiene compatibilidad)
     * @param {File} file - Archivo de imagen
     * @returns {Promise<Object>} Objeto con la URL final del banner
     */
    static async uploadGeneralBanner(file) {
        try {
            return await this.uploadBanner(file);
        } catch (error) {
            console.error('Error en uploadGeneralBanner:', error);
            throw error;
        }
    }

    // ========== MÉTODOS ESPECÍFICOS POR CATÁLOGO ID ==========

    /**
     * Sube un banner para un catálogo específico usando el flujo de signed URLs
     * @param {string} catalogId - ID del catálogo específico
     * @param {File} file - Archivo de imagen
     * @returns {Promise<Object>} Objeto con la URL final del banner
     */
    static async uploadBannerById(catalogId, file) {
        try {
            // 1. Generar signed URL para el catálogo específico
            const { signedUrl, finalUrl } = await this.generateBannerSignedUrlById(catalogId, file);

            // 2. Subir archivo directamente a S3
            const uploadRes = await fetch(signedUrl, {
                method: 'PUT',
                body: file,
                headers: {
                    'Content-Type': file.type,
                },
            });

            if (!uploadRes.ok) {
                throw new Error('Error al subir archivo a S3');
            }

            // 3. Actualizar URL en la base de datos para el catálogo específico
            await this.updateBannerUrlById(catalogId, finalUrl);

            return { bannerUrl: finalUrl };
        } catch (error) {
            console.error('Error en uploadBannerById:', error);
            throw error;
        }
    }

    /**
     * Genera una signed URL para subir banner de un catálogo específico
     * @param {string} catalogId - ID del catálogo específico
     * @param {File} file - Archivo de imagen
     * @returns {Promise<Object>} Objeto con signedUrl y finalUrl
     */
    static async generateBannerSignedUrlById(catalogId, file) {
        try {
            const requestData = {
                mimeType: file.type,
                filename: file.name
            };

            const res = await secureFetch(`${API_BASE_URL}/private-catalogs/${catalogId}/banner/signed-url`, {
                method: 'POST',
                body: JSON.stringify(requestData),
            });

            if (res.ok) {
                return await res.json();
            } else {
                const error = await res.json();
                throw new Error(error.message || 'Error al generar signed URL para catálogo específico');
            }
        } catch (error) {
            console.error('Error en generateBannerSignedUrlById:', error);
            throw error;
        }
    }

    /**
     * Actualiza la URL del banner en un catálogo específico
     * @param {string} catalogId - ID del catálogo específico
     * @param {string} bannerUrl - URL del banner
     * @returns {Promise<Object>} Catálogo actualizado
     */
    static async updateBannerUrlById(catalogId, bannerUrl) {
        try {
            const requestData = { bannerUrl };

            const res = await secureFetch(`${API_BASE_URL}/private-catalogs/${catalogId}/banner/update-url`, {
                method: 'POST',
                body: JSON.stringify(requestData),
            });

            if (res.ok) {
                return await res.json();
            } else {
                const error = await res.json();
                throw new Error(error.message || 'Error al actualizar URL del banner en catálogo específico');
            }
        } catch (error) {
            console.error('Error en updateBannerUrlById:', error);
            throw error;
        }
    }

    /**
     * Obtiene la información pública de un catálogo
     * @param {string|number} providerId - ID del proveedor
     * @returns {Promise<Object>} Información del catálogo
     */
    static async getPublicCatalog(providerId) {
        try {
            const res = await fetch(`${API_BASE_URL}/catalog/${providerId}`);

            if (res.ok) {
                const data = await res.json();
                return data.catalog;
            } else if (res.status === 404) {
                throw new Error('Catálogo no encontrado');
            } else {
                throw new Error('Error al obtener catálogo');
            }
        } catch (error) {
            console.error('Error en getPublicCatalog:', error);
            throw error;
        }
    }

    /**
     * Obtiene los productos de un catálogo público
     * @param {string|number} providerId - ID del proveedor
     * @returns {Promise<Object>} Datos completos del catálogo con banner_url, provider_id y productos
     */
    static async getCatalogProductsPublic(providerId) {
        try {
            const res = await fetch(`${API_BASE_URL}/catalog/${providerId}/products`);

            if (res.ok) {
                const data = await res.json();
                return data; // Devolver el objeto completo con banner_url, provider_id y products
            } else {
                throw new Error('Error al obtener productos del catálogo');
            }
        } catch (error) {
            console.error('Error en getCatalogProductsPublic:', error);
            throw error;
        }
    }

    // ===== MÉTODOS DE COMPATIBILIDAD =====
    // Estos métodos mantienen la API anterior para compatibilidad

    /**
     * Crea o actualiza el catálogo del proveedor - método de compatibilidad
     * @param {Object} catalogData - Datos del catálogo
     * @param {string} [catalogData.internalName] - Nombre interno del catálogo (opcional)
     * @param {string} [catalogData.publicName] - Nombre público del catálogo (opcional)
     * @param {string} [catalogData.description] - Descripción del catálogo (opcional)
     * @param {string} [catalogData.enlace] - Enlace del catálogo (opcional)
     * @returns {Promise<Object>} Catálogo actualizado
     */
    static async updateCatalogLegacy({ internalName, publicName, description, enlace } = {}) {
        return await this.updateGeneralCatalog({ internalName, publicName, description, enlace });
    }

    /**
     * Sube un banner - método de compatibilidad
     * @param {File} file - Archivo de imagen
     * @returns {Promise<Object>} Objeto con la URL final del banner
     */
    static async uploadBannerLegacy(file) {
        return await this.uploadGeneralBanner(file);
    }
}

/**
 * Servicio para gestionar órdenes temporales
 */
class TemporaryOrderService {

    /**
     * Crea una orden temporal
     * @param {Object} orderData - Datos de la orden
     * @param {number} orderData.providerId - ID del proveedor
     * @param {number} orderData.totalAmount - Monto total
     * @param {Array} orderData.items - Items de la orden
     * @param {string} [orderData.customerName] - Nombre del cliente (opcional)
     * @param {string} [orderData.customerPhone] - Teléfono del cliente (opcional)
     * @param {string} [orderData.customerEmail] - Email del cliente (opcional)
     * @param {string} [orderData.customerAddress] - Dirección del cliente (opcional)
     * @param {string} [orderData.notes] - Notas adicionales (opcional)
     * @returns {Promise<Object>} Token y ID de la orden temporal
     */
    static async createTemporaryOrder(orderData) {
        try {
            const res = await fetch(`${API_BASE_URL}/temporary-orders`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(orderData)
            });

            if (res.ok) {
                return await res.json();
            } else {
                const error = await res.json();
                throw new Error(error.message || 'Error al crear orden temporal');
            }
        } catch (error) {
            console.error('Error en createTemporaryOrder:', error);
            throw error;
        }
    }

    /**
     * Obtiene una orden temporal por su token
     * @param {string} token - Token de la orden temporal
     * @returns {Promise<Object>} Datos de la orden temporal
     */
    static async getTemporaryOrder(token) {
        try {
            const res = await fetch(`${API_BASE_URL}/temporary-orders/${token}`);

            if (res.ok) {
                return await res.json();
            } else if (res.status === 404) {
                throw new Error('Orden temporal no encontrada');
            } else {
                throw new Error('Error al obtener orden temporal');
            }
        } catch (error) {
            console.error('Error en getTemporaryOrder:', error);
            throw error;
        }
    }

    /**
     * Genera un mensaje de WhatsApp para una orden temporal
     * @param {Object} order - Datos de la orden temporal
     * @param {string} orderUrl - URL del pedido
     * @returns {string} Mensaje formateado para WhatsApp
     */
    static generateWhatsAppMessage(order, orderUrl) {
        const formatDate = (dateString) => {
            const date = new Date(dateString);
            return date.toLocaleDateString('es-ES', {
                year: 'numeric',
                month: 'long',
                day: 'numeric',
                hour: '2-digit',
                minute: '2-digit'
            });
        };

        let message = `🛒 *PEDIDO TEMPORAL*\n\n`;
        message += `📅 Fecha: ${formatDate(order.createdAt)}\n`;
        message += `💰 Total: $${order.totalAmount.toLocaleString()}\n\n`;

        // Información del cliente (si está disponible)
        if (order.customerName || order.customerPhone || order.customerEmail || order.customerAddress) {
            message += `👤 *CLIENTE:*\n`;
            if (order.customerName) message += `Nombre: ${order.customerName}\n`;
            if (order.customerPhone) message += `Teléfono: ${order.customerPhone}\n`;
            if (order.customerEmail) message += `Email: ${order.customerEmail}\n`;
            if (order.customerAddress) message += `Dirección: ${order.customerAddress}\n`;
            message += `\n`;
        }

        message += `📦 *PRODUCTOS:*\n`;

        order.items.forEach((item, index) => {
            message += `\n${index + 1}. *${item.productName}*\n`;
            message += `   Color: ${item.colorName}\n`;
            message += `   Talla: ${item.sizeName}\n`;
            message += `   Cantidad: ${item.quantity}\n`;
            message += `   Precio: $${item.unitPrice.toLocaleString()} c/u\n`;
            message += `   Subtotal: $${item.totalPrice.toLocaleString()}\n`;
        });

        message += `\n💵 *TOTAL: $${order.totalAmount.toLocaleString()}*\n\n`;

        if (order.notes) {
            message += `📝 *NOTAS:*\n${order.notes}\n\n`;
        }

        message += `🔗 Ver pedido completo: ${orderUrl}`;

        return message;
    }
}

/**
 * Servicio para operaciones de usuario/proveedor
 */
class UserService {

    /**
     * Obtiene el perfil del usuario autenticado
     * @returns {Promise<Object>} Datos del perfil
     */
    static async getProfile() {
        try {
            const res = await secureFetch(`${API_BASE_URL}/profile`);

            if (res.ok) {
                return await res.json();
            } else {
                throw new Error('Error al obtener perfil');
            }
        } catch (error) {
            console.error('Error en getProfile:', error);
            throw error;
        }
    }

    /**
     * Verifica si el usuario actual es un proveedor (mismo patrón que MyProducts)
     * @returns {boolean} True si es proveedor
     */
    static isProvider() {
        const user = PrivateCatalogService.getUserFromStorage();
        return !!(user?.proveedorInfo?.id);
    }

    /**
     * Obtiene la información del proveedor actual (mismo patrón que MyProducts)
     * @returns {Object|null} Información del proveedor o null
     */
    static getProviderInfo() {
        return PrivateCatalogService.getProviderFromStorage();
    }
}

/**
 * Utilidades generales
 */
class Utils {

    /**
     * Formatea un número como moneda colombiana
     * @param {number} amount - Cantidad a formatear
     * @returns {string} Cantidad formateada
     */
    static formatCurrency(amount) {
        return new Intl.NumberFormat('es-CO', {
            style: 'currency',
            currency: 'COP',
            minimumFractionDigits: 0,
            maximumFractionDigits: 0,
        }).format(amount);
    }

    /**
     * Formatea una fecha
     * @param {string} dateString - Fecha en formato ISO
     * @param {Object} options - Opciones de formateo
     * @returns {string} Fecha formateada
     */
    static formatDate(dateString, options = {}) {
        const defaultOptions = {
            year: 'numeric',
            month: 'long',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        };

        const date = new Date(dateString);
        return date.toLocaleDateString('es-ES', { ...defaultOptions, ...options });
    }

    /**
     * Copia texto al portapapeles
     * @param {string} text - Texto a copiar
     * @returns {Promise<boolean>} Éxito de la operación
     */
    static async copyToClipboard(text) {
        try {
            await navigator.clipboard.writeText(text);
            return true;
        } catch (error) {
            console.error('Error al copiar al portapapeles:', error);

            // Fallback para navegadores que no soportan clipboard API
            try {
                const textArea = document.createElement('textarea');
                textArea.value = text;
                textArea.style.position = 'fixed';
                textArea.style.left = '-999999px';
                textArea.style.top = '-999999px';
                document.body.appendChild(textArea);
                textArea.focus();
                textArea.select();
                const successful = document.execCommand('copy');
                
                // Usar setTimeout para asegurar que el comando se procese antes de remover
                setTimeout(() => {
                    try {
                        if (textArea.parentNode === document.body) {
                            document.body.removeChild(textArea);
                        }
                    } catch (e) {
                        console.error('Error al limpiar textarea:', e);
                    }
                }, 100);
                
                return successful;
            } catch (fallbackError) {
                console.error('Error en fallback de clipboard:', fallbackError);
                return false;
            }
        }
    }

    /**
     * Extrae cantidades de un string de descripción de precios
     * @param {string} condition - Descripción de condiciones
     * @returns {Array<number>} Array de cantidades
     */
    static extractQuantitiesFromCondition(condition) {
        if (!condition) return [];

        const matches = condition.match(/\d+/g);

        if (matches) {
            return matches.map(num => parseInt(num)).sort((a, b) => a - b);
        }

        return [];
    }

    /**
     * Convierte array de precios de base de datos al formato requerido por el frontend
     * @param {Array} prices - Array de precios de la base de datos
     * @returns {Object} Objeto con cantidades como claves y precios como valores
     */
    static formatPricesForFrontend(prices) {
        if (!prices || !Array.isArray(prices)) return {};

        const formattedPrices = {};

        prices.forEach(price => {
            const amount = parseFloat(price.amount);
            const quantities = this.extractQuantitiesFromCondition(price.condition);

            quantities.forEach(qty => {
                formattedPrices[qty] = amount;
            });
        });

        return formattedPrices;
    }
}

// Exportar todos los servicios
export {
    PrivateCatalogService,
    TemporaryOrderService,
    UserService,
    Utils
};

// Exportación por defecto para compatibilidad
const PrivateCatalogServices = {
    PrivateCatalogService,
    TemporaryOrderService,
    UserService,
    Utils
};

export default PrivateCatalogServices;